/**
 * CSV Export Utilities
 * Exports canonical bill data to CSV with UTF-8 BOM for Excel compatibility
 */

import JSZip from 'jszip';
import type { BillHeader, LineItem } from './parserTransform';

const BOM = '\uFEFF';

/**
 * Escape CSV value
 */
function escapeCSV(value: any): string {
  if (value === null || value === undefined) return '';
  
  const str = String(value);
  
  // Escape if contains comma, quote, or newline
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  
  return str;
}

/**
 * Check if a value is valid (not null, empty, or undefined)
 */
function isValidValue(value: any): boolean {
  if (value === null || value === undefined || value === '') return false;
  if (typeof value === 'string' && value.trim() === '') return false;
  return true;
}

/**
 * Generate header CSV content - dynamically extract all fields from raw data
 */
function generateHeaderCSV(header: BillHeader, rawData: any, userDefinedFields: any[] = []): string {
  // Helper function to format field labels
  const formatFieldLabel = (key: string): string => {
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Helper function to format field values
  const formatFieldValue = (value: any): string => {
    if (typeof value === 'number') {
      if (value % 1 !== 0) {
        return value.toFixed(2);
      }
      return value.toString();
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    if (Array.isArray(value)) {
      return value.length > 0 ? value.join(', ') : '';
    }
    return String(value);
  };

  // Extract all fields from the raw API response
  const extractAllFields = (data: any): Array<{key: string, label: string, value: any}> => {
    const fields: Array<{key: string, label: string, value: any}> = [];
    
    if (!data || !data.data || !data.data.results) return fields;

    const results = data.data.results;
    
    // Extract top-level fields from results
    Object.entries(results).forEach(([key, value]) => {
      if (isValidValue(value) && !Array.isArray(value) && typeof value !== 'object') {
        fields.push({
          key: key,
          label: formatFieldLabel(key),
          value: value
        });
      }
    });

    // Sort fields by importance
    const fieldPriority = [
      'customerName', 'supplierName', 'invoiceDate', 'invoiceNumber', 'accountNumber',
      'dueDate', 'currentBillAmount', 'totalAmountDue', 'billingPeriod', 'address',
      'city', 'state', 'postCode', 'country', 'currency', 'paymentMethods'
    ];

    fields.sort((a, b) => {
      const aIndex = fieldPriority.indexOf(a.key);
      const bIndex = fieldPriority.indexOf(b.key);
      
      if (aIndex === -1 && bIndex === -1) return a.label.localeCompare(b.label);
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });

    return fields;
  };

  // Get all extracted fields from the raw data
  const allFields = extractAllFields(rawData);
  
  // Add meter data fields
  const meterFields = Object.entries(header.meter_data || {}).map(([key, value]) => ({
    key: `meter_${key}`,
    label: `Meter ${formatFieldLabel(key)}`,
    value: value
  }));
  
  // Add identifier data fields
  const identifierFields = Object.entries(header.identifier_data || {}).map(([key, value]) => ({
    key: `identifier_${key}`,
    label: `Identifier ${formatFieldLabel(key)}`,
    value: value
  }));
  
  // Add user-defined fields
  const userFields = userDefinedFields.map(field => ({
    key: `user_${field.name}`,
    label: `User ${field.name}`,
    value: field.value
  }));
  
  // Combine all fields
  const combinedFields = [...allFields, ...meterFields, ...identifierFields, ...userFields];
  
  const columns = combinedFields.map(field => field.key);
  const values = combinedFields.map(field => formatFieldValue(field.value));
  
  const headerRow = columns.map(escapeCSV).join(',');
  const dataRow = values.map(escapeCSV).join(',');
  
  return BOM + headerRow + '\n' + dataRow;
}

/**
 * Generate line items CSV content - only include items with valid data
 */
function generateLineItemsCSV(lineItems: LineItem[], fileId: string): string {
  // Filter out line items that don't have meaningful data
  const validLineItems = lineItems.filter(item => 
    isValidValue(item.description) || 
    isValidValue(item.quantity) || 
    isValidValue(item.unit_price) || 
    isValidValue(item.amount)
  );

  if (validLineItems.length === 0) {
    return BOM + 'file_id,row_index,description,quantity,unit_price,amount\n';
  }

  const columns = [
    'file_id',
    'row_index',
    'description',
    'quantity',
    'unit_price',
    'amount',
  ];
  
  const headerRow = columns.map(escapeCSV).join(',');
  
  const dataRows = validLineItems.map(item => {
    const values = [
      fileId,
      item.file_row_index,
      item.description || '',
      item.quantity || '',
      item.unit_price || '',
      item.amount || '',
    ];
    
    return values.map(escapeCSV).join(',');
  });
  
  return BOM + headerRow + '\n' + dataRows.join('\n');
}

/**
 * Export bill data as zipped CSV files
 */
export async function exportBillCSV(
  header: BillHeader,
  lineItems: LineItem[],
  rawData: any,
  fileName = 'extracted_data',
  userDefinedFields: any[] = []
): Promise<Blob> {
  const fileId = `bill_${Date.now()}`;
  
  const headerCSV = generateHeaderCSV(header, rawData, userDefinedFields);
  const lineItemsCSV = generateLineItemsCSV(lineItems, fileId);
  
  const zip = new JSZip();
  zip.file(`${fileName}_header.csv`, headerCSV);
  zip.file(`${fileName}_line_items.csv`, lineItemsCSV);
  
  return await zip.generateAsync({ type: 'blob' });
}

/**
 * Download zipped CSV files
 */
export async function downloadBillCSV(
  header: BillHeader,
  lineItems: LineItem[],
  rawData: any,
  fileName = 'extracted_data',
  userDefinedFields: any[] = []
): Promise<void> {
  const blob = await exportBillCSV(header, lineItems, rawData, fileName, userDefinedFields);
  
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${fileName}_${new Date().toISOString().split('T')[0]}.zip`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
